import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card } from '@components/ui';
import { useVelocityData } from '@hooks';

export default function VelocityChart({ projectId, height = 300 }) {
  const { data: velocityData, isLoading, error } = useVelocityData(projectId);

  const chartData = useMemo(() => {
    if (!velocityData?.data) return null;
    return velocityData.data;
  }, [velocityData]);

  if (isLoading) {
    return (
      <Card padding="lg">
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          📊 Velocity Chart
        </h3>
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>Cargando...</p>
        </div>
      </Card>
    );
  }

  if (error || !chartData || chartData.velocity.length === 0) {
    return (
      <Card padding="lg">
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          📊 Velocity Chart
        </h3>
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>
            {error ? 'Error al cargar datos' : 'No hay sprints completados'}
          </p>
        </div>
      </Card>
    );
  }

  const { velocity, averageVelocity } = chartData;
  const maxPoints = Math.max(...velocity.map((s) => s.completedPoints), averageVelocity) * 1.2;
  const barWidth = 80 / velocity.length;
  const barGap = barWidth * 0.3;
  const actualBarWidth = barWidth - barGap;

  return (
    <Card padding="lg">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
          📊 Velocity Chart
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Promedio:</span>
          <span
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#667eea',
              backgroundColor: '#667eea15',
              padding: '4px 10px',
              borderRadius: '8px',
            }}
          >
            {averageVelocity} pts
          </span>
        </div>
      </div>

      <div style={{ position: 'relative', width: '100%', height }}>
        <svg
          viewBox="0 0 100 60"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: '100%' }}
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
            <line
              key={i}
              x1="10"
              y1={50 - ratio * 40}
              x2="95"
              y2={50 - ratio * 40}
              stroke="#e2e8f0"
              strokeWidth="0.2"
            />
          ))}

          {/* Y-axis labels */}
          {[0, 0.5, 1].map((ratio, i) => (
            <text
              key={i}
              x="8"
              y={51 - ratio * 40}
              textAnchor="end"
              style={{ fontSize: '3px', fill: '#64748b' }}
            >
              {Math.round(maxPoints * ratio)}
            </text>
          ))}

          {/* Average line */}
          <line
            x1="10"
            y1={50 - (averageVelocity / maxPoints) * 40}
            x2="95"
            y2={50 - (averageVelocity / maxPoints) * 40}
            stroke="#f59e0b"
            strokeWidth="0.3"
            strokeDasharray="1,0.5"
          />

          {/* Bars */}
          {velocity.map((sprint, i) => {
            const x = 12 + i * barWidth;
            const barHeight = (sprint.completedPoints / maxPoints) * 40;
            const y = 50 - barHeight;

            return (
              <g key={sprint.sprintId}>
                {/* Bar */}
                <rect
                  x={x}
                  y={y}
                  width={actualBarWidth}
                  height={barHeight}
                  fill="url(#barGradient)"
                  rx="0.5"
                />
                {/* Value label */}
                <text
                  x={x + actualBarWidth / 2}
                  y={y - 1}
                  textAnchor="middle"
                  style={{ fontSize: '2.5px', fill: '#475569', fontWeight: '600' }}
                >
                  {sprint.completedPoints}
                </text>
                {/* Sprint name */}
                <text
                  x={x + actualBarWidth / 2}
                  y="55"
                  textAnchor="middle"
                  style={{ fontSize: '2px', fill: '#64748b' }}
                >
                  {sprint.sprintName.length > 10 ? sprint.sprintName.slice(0, 8) + '...' : sprint.sprintName}
                </text>
              </g>
            );
          })}

          {/* Gradient definition */}
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#667eea" />
              <stop offset="100%" stopColor="#764ba2" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '16px',
              height: '12px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              borderRadius: '2px',
            }}
          />
          <span style={{ fontSize: '12px', color: '#64748b' }}>Puntos completados</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '2px', backgroundColor: '#f59e0b' }} />
          <span style={{ fontSize: '12px', color: '#64748b' }}>Promedio</span>
        </div>
      </div>
    </Card>
  );
}

VelocityChart.propTypes = {
  projectId: PropTypes.number,
  height: PropTypes.number,
};
